import { Flex, LayoutProps, Text } from '@chakra-ui/react'
import React, { useCallback, useMemo, useState } from 'react'
import IconDropdownDownArrow from '~/assets/icons/icon-dropdown-downArrow.svg?react'
import IconDropdownUpArrow from '~/assets/icons/icon-dropdown-upArrow.svg?react'
import { useModalState } from '~/atoms/ModalState'
import { useSettingsState } from '~/atoms/SettingsState'
import { useUserState } from '~/atoms/UserState'
import { TextButtonVariants } from '~/components/shared/componentStyles/TextButton.style'
import { DropdownTextButton } from '~/components/shared/Dropdown/DropdownTextButton'
import { MenuPlacement } from '~/components/shared/Dropdown/Menu'
import { MenuItem, MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { useAnalytics } from '~/hooks/useAnalytics'
import { QueueAgentStatus, useCallQueue } from '~/hooks/useCallQueue'
import { useQueueLogout } from '~/queries/queueQueries'
import { AnalyticsNamespace, WebPhoneAnalyticsEvents } from '~/types/Analytics'

import { ExtensionInstance } from '@mangovoice/types'

import { useExtensionSelectorStyles } from './componentStyles/ExtensionSelector.style'

interface ExtensionSelectorProps {
  hasActiveCall: boolean
  layoutProps?: LayoutProps
}

const UNLINK_DEVICE_WARNING_KEY = 'unlinkDeviceWarning'

export const ExtensionSelector = ({
  hasActiveCall,
  layoutProps,
}: ExtensionSelectorProps) => {
  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const {
    currentUserExtension,
    currentUserExtensionId,
    pbxExtensions,
    setCurrentUserExtension,
    pbxId,
  } = useUserState()
  const { setWebPhoneEnabled } = useSettingsState()
  const {
    confirmModal: [, setConfirmModalState],
    callInProgressModal: [, setCallInProgressModalState],
  } = useModalState()
  const { updateStatus, currentAgentStatus } = useCallQueue()
  const queueLogout = useQueueLogout({
    extensionId: currentUserExtensionId,
    onQuerySuccess: () => {
      if (currentAgentStatus === QueueAgentStatus.InQueue) {
        updateStatus(QueueAgentStatus.OutOfQueue)
      }
    },
  })
  const styles = useExtensionSelectorStyles()

  const [extDropdownOpen, setExtDropdownOpen] = useState<boolean>(false)
  const [hovered, setHovered] = useState(false)

  const showUnlinkDeviceWarning = useCallback(
    (ext: ExtensionInstance) => {
      setConfirmModalState({
        title: `Unlink Device`,
        message: (
          <Flex
            sx={{
              flexDirection: 'column',
              gap: '.5rem',
              alignItems: 'center',
            }}
          >
            <Text sx={{ fontSize: '1rem', fontWeight: '400' }}>
              This device will no longer send or receive calls from{' '}
              {currentUserExtension?.name}.
            </Text>
          </Flex>
        ),
        confirmText: 'Ok',
        cancelText: 'Cancel',
        closeAfterConfirm: true,
        dontShowAgainKey: UNLINK_DEVICE_WARNING_KEY,
        onConfirm: () => {
          track(WebPhoneAnalyticsEvents.ExtSwitched)
          setCurrentUserExtension(pbxId, ext)
        },
      })
    },
    [setCurrentUserExtension, currentUserExtension],
  )

  const handleSelect = useCallback(
    (ext: ExtensionInstance) => {
      if (ext.id === currentUserExtension?.id) {
        return
      }
      if (hasActiveCall) {
        setCallInProgressModalState({ open: true })
      } else {
        showUnlinkDeviceWarning(ext)
      }
    },
    [hasActiveCall, setCurrentUserExtension, showUnlinkDeviceWarning],
  )

  const handleDeactivateSelect = useCallback(() => {
    if (hasActiveCall) {
      setCallInProgressModalState({ open: true })
    } else {
      track(WebPhoneAnalyticsEvents.Deactivated)
      if (currentUserExtension) {
        queueLogout()
      }
      setCurrentUserExtension(pbxId, undefined)
      setWebPhoneEnabled(false)
    }
  }, [hasActiveCall, setWebPhoneEnabled])

  const handleExtMenuOpenChange = () => {
    setExtDropdownOpen(!extDropdownOpen)
    return !extDropdownOpen
  }

  const extMenuItems = useMemo(() => {
    const extensions =
      pbxExtensions?.map((ext) => {
        return {
          value: ext,
          content: (
            <MenuListItem
              primaryText={ext.name}
              rightContent={<ExtensionBadge extension={ext.number} />}
            />
          ),
          onSelect: () => handleSelect(ext),
        } as MenuItem<ExtensionInstance>
      }) ?? []

    extensions.push({
      value: { name: 'None', number: '' } as ExtensionInstance,
      content: (
        <MenuListItem primaryText={'Deactivate Web Phone'} isDestructive />
      ),
      onSelect: handleDeactivateSelect,
    })

    return extensions
  }, [pbxExtensions, handleSelect, handleDeactivateSelect])

  return (
    <Flex h={layoutProps?.h} sx={styles.container}>
      <Flex
        sx={styles.extensionBadge}
        border={hovered ? '2px solid #5462F8' : '2px solid #B4B4CC'}
      >
        <Text
          sx={styles.extensionBadgeText}
          color={hovered ? '#0A1DE9' : '#1D1D30'}
        >
          {currentUserExtension?.number}
        </Text>
      </Flex>
      <DropdownTextButton
        placeholder={currentUserExtension?.name}
        items={extMenuItems}
        rightIcon={
          extDropdownOpen ? IconDropdownUpArrow : IconDropdownDownArrow
        }
        onMenuOpenChange={handleExtMenuOpenChange}
        menuOptions={{
          width: '20rem',
          placement: MenuPlacement.BottomRight,
        }}
        variant={TextButtonVariants.Gray_1600}
        header={
          <Flex sx={styles.header}>
            <Text sx={styles.headerText}>Select A New Extension</Text>
          </Flex>
        }
        hoverChanged={(hovered) => {
          setHovered(hovered)
        }}
      />
    </Flex>
  )
}
